import { enqueueSnackbar } from 'notistack';
import React, { Activity, useState, useEffect } from 'react';

import {
  IconTrash,
  IconMoodKid,
  IconDevices,
  IconClockHour4,
} from '@tabler/icons-react';

import ConfirmationModal from '@/components/ConfirmationModal';
import CustomModal from '@/components/CustomModal';
import { useFormatText } from '@/hooks/useFormatText';
import { removeDevice, getDeviceDetail } from '@/service/controlService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { IDeviceDetail } from '@/types/device';
import { formatLastActive } from '@/utils';

interface IDeviceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  deviceId: string | null;
  onSuccess?: () => void;
}

const DETAIL_PREFIX = 'controls.device-management.details';

const DeviceDetailsModal: React.FC<IDeviceDetailsModalProps> = ({
  isOpen,
  onClose,
  deviceId,
  onSuccess,
}) => {
  const { formatText } = useFormatText();
  const { isDrawerLoading, setDrawerLoading } = useSkeletonStore();
  const [deviceDetail, setDeviceDetail] = useState<IDeviceDetail | null>(null);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    const fetchDeviceDetail = async () => {
      if (!isOpen || !deviceId) return;

      setDrawerLoading(true);
      try {
        const res = await getDeviceDetail(deviceId);
        if (res?.status && res?.data) {
          setDeviceDetail(res.data);
        } else {
          setDeviceDetail(null);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setDrawerLoading(false);
      }
    };

    fetchDeviceDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, deviceId]);

  const handleConfirmRemove = async () => {
    if (!deviceId) return;
    setIsRemoving(true);
    try {
      const res = await removeDevice(deviceId);
      if (res?.status) {
        enqueueSnackbar(res?.message, { variant: 'success' });
        setIsRemoveModalOpen(false);
        onClose();
        onSuccess?.();
      } else {
        enqueueSnackbar(res?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsRemoving(false);
    }
  };

  if (!isOpen || !deviceId) return null;

  const detailRows = deviceDetail
    ? [
        {
          label: formatText(`${DETAIL_PREFIX}.device-model`),
          value: deviceDetail.device_model,
        },
        {
          label: formatText(`${DETAIL_PREFIX}.operating-system`),
          value: [deviceDetail.os, deviceDetail.os_version]
            .filter(Boolean)
            .join(' '),
        },
        {
          label: formatText(`${DETAIL_PREFIX}.app-version`),
          value: deviceDetail.app_version,
        },
        {
          label: formatText(`${DETAIL_PREFIX}.device-type`),
          value: deviceDetail.device_type || deviceDetail.device_name,
        },
      ]
    : [];

  return (
    <>
      <CustomModal
        setIsOpen={onClose}
        heading={formatText(`${DETAIL_PREFIX}.title`)}
        isHeader
        isFooter
        position="right"
        className="w-full max-w-134.25"
        childClassName="p-6! gap-6!"
        cancelButtonProps={{
          label: formatText(`${DETAIL_PREFIX}.remove-device`),
          desructive: true,
          icon: <IconTrash size={16} />,
          className: '!text-error-default',
          onClick: () => setIsRemoveModalOpen(true),
        }}
        confirmButtonProps={{
          label: formatText(`${DETAIL_PREFIX}.done`),
          variant: 'primary',
          onClick: () => onClose(),
        }}
        dataTestId="device-details-modal"
      >
        {isDrawerLoading || !deviceDetail ? (
          <div className="w-full space-y-4 animate-pulse">
            <div className="h-24 bg-neutral-light-50 rounded-2xl" />
            <div className="space-y-5 pt-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex justify-between">
                  <div className="h-4 w-32 bg-neutral-light-50 rounded" />
                  <div className="h-4 w-24 bg-neutral-light-50 rounded" />
                </div>
              ))}
            </div>
            <div className="flex gap-12 pt-2">
              <div className="h-12 w-36 bg-neutral-light-50 rounded" />
              <div className="h-12 w-36 bg-neutral-light-50 rounded" />
            </div>
          </div>
        ) : (
          <div className="w-full space-y-8">
            {/* Overview Card */}
            <div className="bg-purple-50 rounded-2xl p-6 space-y-5">
              {/* Device ID row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="bg-brand-default rounded-lg p-2">
                    <IconDevices size={18} className="text-white" />
                  </div>
                  <span className="text-base font-semibold text-neutral-primary">
                    {formatText(`${DETAIL_PREFIX}.device-id`)}
                  </span>
                </div>
                <span className="text-base font-semibold text-neutral-primary">
                  {deviceDetail.device_id || '-'}
                </span>
              </div>

              {/* Detail rows */}
              {detailRows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between"
                >
                  <span className="text-sm text-neutral-secondary">
                    {row.label}
                  </span>
                  <span className="text-sm font-semibold text-neutral-primary">
                    {row.value || '-'}
                  </span>
                </div>
              ))}
            </div>

            {/* Linked Child & Last Active */}
            <div className="grid grid-cols-2 gap-4 px-1">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 text-neutral-secondary">
                  <IconMoodKid size={20} />
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm text-neutral-secondary">
                    {formatText(`${DETAIL_PREFIX}.linked-child`)}
                  </span>
                  <span className="text-base font-semibold text-neutral-primary max-w-65 capitalize">
                    {deviceDetail.linked_child || '-'}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="mt-0.5 text-neutral-secondary">
                  <IconClockHour4 size={20} />
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm text-neutral-secondary">
                    {formatText(`${DETAIL_PREFIX}.last-active`)}
                  </span>
                  <span className="text-base font-semibold text-neutral-primary">
                    {deviceDetail.last_active
                      ? formatLastActive(deviceDetail.last_active, formatText)
                      : '-'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CustomModal>

      {/* Remove Device Confirmation Modal */}
      <Activity mode={isRemoveModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isRemoveModalOpen}
          setIsOpen={setIsRemoveModalOpen}
          title={formatText(`${DETAIL_PREFIX}.remove-modal.title`)}
          descriptionPrefix={formatText(
            `${DETAIL_PREFIX}.remove-modal.description-prefix`
          )}
          descriptionSuffix={`${DETAIL_PREFIX}.remove-modal.description-suffix`}
          entityName={deviceDetail?.device_id || ''}
          confirmLabel={formatText(`${DETAIL_PREFIX}.remove-modal.confirm`)}
          cancelLabel={formatText('common.actions.cancel')}
          onConfirm={handleConfirmRemove}
          isDestructive
          isLoading={isRemoving}
          dataTestId="remove-device-modal"
        />
      </Activity>
    </>
  );
};

export default DeviceDetailsModal;
